import { ApoliceLogo } from '@/core/domain/porto-seguro/apolices/apolices.types';
import { useWorkorderDetailContext } from '../../WorkorderDetailProvider';
import { Card, Empty, Form, Modal, Skeleton } from 'antd';
import { usePortoPoliciesList } from '@/core/domain/porto/policies/porto-policies.hooks';
import { useDisclosure } from '@/hooks';
import { useEffect } from 'react';
import { WorkOrderComplementStatus } from '@/core/domain/workorder/WorkOrdersComplements/WorkOrdersComplements.types';
import PortoPolicyCard from '@/components/_common/PortoPolicyCard';

interface FormValues {
  policy: {
    companyCode?: string;
    policyNumber?: string;
    customer?: {
      birthDate?: string;
      documentNumber?: string;
      contacts?: { email?: string; phone?: string };
      name?: string;
    };
    vehicle?: { chassi?: string; brand?: string; model?: string };
  };
}

export default function ComplemetPolicy() {
  const {
    workorder: { data: workOrder },
    complement: { data: complement },
  } = useWorkorderDetailContext();

  const isCloseOrCanceld =
    complement?.status === WorkOrderComplementStatus.Closed ||
    complement?.status === WorkOrderComplementStatus.Cancelled;

  const hasPolicy = !!complement?.policyNumber;
  const policyNumber = complement?.policyNumber;

  const licensePlate = workOrder?.vehicle?.licensePlate;

  const [opened, action] = useDisclosure(false);

  const [form] = Form.useForm<FormValues>();
  const policyNumberField = Form.useWatch(['policy', 'policyNumber'], form);

  const policyList = usePortoPoliciesList(licensePlate, opened && hasPolicy);

  const handleClose = () => {
    action.close();
  };

  const changeConfirm = async () => {
    try {
      Modal.confirm({
        title: 'Alteração de apólice',
        content:
          'Caso haja benefícios selecionados, os mesmos serão removidos do orçamento',
        okText: 'Confirmar',
      });
    } catch {}
  };

  useEffect(() => {
    if (policyNumber && policyList.data?.length) {
      const policy = policyList.data.find(
        (policy) => policy.policyNumber === policyNumber,
      );

      if (!policy) return;

      const { email, phone, ...restCustomer } = policy.customer;

      form.setFieldsValue({
        policy: {
          companyCode: policy.companyCode,
          policyNumber: policy.policyNumber,
          customer: {
            ...restCustomer,
            contacts: email || phone ? { email, phone } : {},
          },
          vehicle: policy.vehicle,
        },
      });
    }
  }, [policyNumber, policyList.data, opened]);

  if (!hasPolicy || !complement?.companyCode) return null;

  return (
    <>
      <a onClick={action.open} style={{ display: 'block' }}>
        <img src={ApoliceLogo[complement?.companyCode]} />
      </a>

      <Modal
        title="Apolice selecionada"
        open={opened}
        onCancel={handleClose}
        okText="Alterar apólice"
        onOk={changeConfirm}
        okButtonProps={{
          disabled:
            !policyNumberField ||
            policyNumberField === policyNumber ||
            isCloseOrCanceld,
        }}
        footer={null}
      >
        <Form form={form}>
          <Form.Item
            hidden
            name={['policy', 'companyCode']}
            rules={
              !!policyList.data?.length
                ? [{ required: true, message: 'Selecione uma apólice' }]
                : undefined
            }
          />
          <Form.Item hidden name={['policy', 'policyNumber']} />
          <Form.Item hidden name={['policy', 'customer']} />
          <Form.Item hidden name={['policy', 'vehicle']} />
          <span>Se necessário selecione uma nova apólice para alterar</span>

          <Skeleton loading={policyList.isLoading}>
            <div style={{ marginTop: 24 }}>
              {!policyList.data?.length && !!licensePlate && (
                <Card style={{ borderRadius: 8 }}>
                  <Empty
                    description={`Não foi encontrado apólices para a placa "${licensePlate}"`}
                  />
                </Card>
              )}

              {policyList.data?.map((policy) => (
                <PortoPolicyCard
                  style={{ marginTop: 16 }}
                  key={policy.policyNumber}
                  active={policy.policyNumber === policyNumber}
                  onClick={() => {
                    if (isCloseOrCanceld) return;

                    form.setFields([{ name: 'customerId', errors: [] }]);

                    if (policyNumber === policy.policyNumber) {
                      form.setFieldValue('policy', undefined);
                    } else {
                      const { email, phone, ...restCustomer } = policy.customer;

                      form.setFieldsValue({
                        policy: {
                          companyCode: policy.companyCode,
                          policyNumber: policy.policyNumber,
                          customer: {
                            ...restCustomer,
                            contacts: { email, phone },
                          },
                          vehicle: policy.vehicle,
                        },
                      });
                    }
                  }}
                  policy={policy}
                  vehicle={{
                    ...policy.vehicle,
                    licensePlate: licensePlate as string,
                  }}
                />
              ))}
            </div>
          </Skeleton>

          <Form.Item shouldUpdate noStyle>
            {({ getFieldError }) => {
              const errors = getFieldError(['policy', 'companyCode']);
              return errors.length ? (
                <span style={{ color: 'red' }}>{errors[0]}</span>
              ) : null;
            }}
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}
